import { create } from 'zustand';
import * as tus from 'tus-js-client';
import { uploadRecording, retryUpload } from '../services/screenRecordingUpload';
import * as recordingStorage from '../services/recordingStorage';
import { supabase } from '../supabase';
import { useScreenRecordingsStore } from './screenRecordingsStore';

/**
 * Queue item — tiene SOLO un riferimento a `recordingId` + stato runtime.
 * Il blob effettivo vive in OPFS (`recordingStorage`) e viene letto on-demand
 * dal service `screenRecordingUpload`. La metadata persistita (title, mime,
 * dimensioni, ...) sta in DB (`screen_recordings` row in stato `uploading`).
 *
 * Per ottenere title/duration/size in UI, leggere da `screenRecordingsStore`
 * via `getRowById(recordingId)`.
 */
export interface QueueItem {
  recordingId: string;
  status: 'queued' | 'uploading' | 'completed' | 'failed';
  progress: number;
  error: string | null;
  upload: tus.Upload | null;
}

interface UploadQueueState {
  items: Record<string, QueueItem>;

  /** Avvia upload TUS leggendo il blob da OPFS. Non bloccante. */
  enqueue: (recordingId: string) => Promise<void>;
  /** Pulisce error fields e ri-tenta upload. */
  retry: (recordingId: string) => Promise<void>;
  /** Aborta upload tus.Upload in corso e rimuove dalla coda. */
  cancel: (recordingId: string) => void;
  /** Silent cleanup: rimuove dalla coda senza abort (es. dopo completed). */
  remove: (recordingId: string) => void;
  /** Selector: true se almeno un item è in stato 'uploading'. Per beforeunload guard. */
  hasActiveUploads: () => boolean;
}

type SetState = (
  partial:
    | Partial<UploadQueueState>
    | ((state: UploadQueueState) => Partial<UploadQueueState>),
) => void;

function setItem(set: SetState, recordingId: string, patch: Partial<QueueItem>): void {
  set((s) => {
    const current = s.items[recordingId];
    if (!current) return {};
    return {
      items: {
        ...s.items,
        [recordingId]: { ...current, ...patch },
      },
    };
  });
}

function makeCallbacks(set: SetState, recordingId: string) {
  return {
    onProgress: (percent: number) => {
      setItem(set, recordingId, { progress: percent, status: 'uploading' });
    },
    onSuccess: () => {
      setItem(set, recordingId, { status: 'completed', progress: 100 });
      // Trigger gallery refresh: la DB row è ora `ready`, ma lo store screenRecordings
      // ha ancora la copia con `status='uploading'`.
      void useScreenRecordingsStore.getState().fetchMine?.();
    },
    onError: (err: Error) => {
      setItem(set, recordingId, {
        status: 'failed',
        error: err.message || 'Errore upload',
      });
    },
  };
}

function deleteItem(set: SetState, recordingId: string): void {
  set((s) => {
    if (!s.items[recordingId]) return {};
    const next = { ...s.items };
    delete next[recordingId];
    return { items: next };
  });
}

export const useUploadQueueStore = create<UploadQueueState>((set, get) => ({
  items: {},

  enqueue: async (recordingId) => {
    set((s) => ({
      items: {
        ...s.items,
        [recordingId]: {
          recordingId,
          status: 'uploading',
          progress: 0,
          error: null,
          upload: null,
        },
      },
    }));

    try {
      const upload = await uploadRecording(recordingId, makeCallbacks(set, recordingId));
      setItem(set, recordingId, { upload });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setItem(set, recordingId, { status: 'failed', error: message });
    }
  },

  retry: async (recordingId) => {
    // Reset stato in coda; se non era presente lo creiamo
    set((s) => ({
      items: {
        ...s.items,
        [recordingId]: {
          recordingId,
          status: 'uploading',
          progress: 0,
          error: null,
          upload: s.items[recordingId]?.upload ?? null,
        },
      },
    }));

    try {
      const upload = await retryUpload(recordingId, makeCallbacks(set, recordingId));
      setItem(set, recordingId, { upload });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setItem(set, recordingId, { status: 'failed', error: message });
    }
  },

  cancel: (recordingId) => {
    const item = get().items[recordingId];
    if (item?.upload) {
      try {
        item.upload.abort();
      } catch (e) {
        console.warn('[uploadQueue] abort error (ignored):', e);
      }
    }
    // Cleanup OPFS + DB row (fire-and-forget): "Annulla" = annulla davvero.
    // Senza questo, OPFS riemerge al refresh via RecoveryDialog e la DB row
    // resta orfana in stato `uploading` (mostrata in gallery come "Upload fallito").
    void recordingStorage.deleteRecording(recordingId).catch(() => undefined);
    void supabase.from('screen_recordings').delete().eq('id', recordingId).then(() => undefined);
    deleteItem(set, recordingId);
    // Trigger gallery refresh per rimuovere la riga orfana dalla UI
    void useScreenRecordingsStore.getState().fetchMine?.();
  },

  remove: (recordingId) => {
    deleteItem(set, recordingId);
  },

  hasActiveUploads: () => {
    return Object.values(get().items).some((i) => i.status === 'uploading');
  },
}));
